import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Byline } from "@/components/Byline";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";
import { SITE } from "@/lib/seo";
import jsonLd from "@/data/confidential-paper-binned-by-mistake-jsonld.json";

/**
 * Dedicated article route modelled on the ICO guide route. Copy is the approved
 * article verbatim; JSON-LD is emitted exactly as supplied in the data file.
 */

const SLUG = "confidential-paper-binned-by-mistake-zagreb-lesson";
const URL = `${SITE}/blog/${SLUG}`;
const TITLE_TAG = "Confidential Paper Binned by Mistake | Preserve Earth";
const META_DESCRIPTION =
  "Zagreb staff records meant for shredding ended up in a street bin. What UK firms should do with confidential paper while it waits for destruction.";
const H1 = "Confidential papers ended up in a street bin. Could it happen in your office?";
const HERO_SRC = "/images/blog/confidential-paper-binned-by-mistake-hero.webp";
const OG_IMAGE = `${SITE}/images/blog/confidential-paper-binned-by-mistake-og.jpg`;
const HERO_ALT =
  "Locked confidential waste console beside a desk of paper files in an office, with a cleaning trolley in the background";
const DATE = "2026-10-20";
const AUTHOR = "Preserve Earth team";
const QUOTE_HREF = "https://preserveearth.co.uk/services/confidential-waste-disposal#enquire";
const ICO_GUIDE = "https://preserveearth.co.uk/blog/ico-confidential-waste-disposal-guide";

const crumbs = [
  { name: "Blog", path: "/blog" },
  { name: H1, path: `/blog/${SLUG}` },
];

export const Route = createFileRoute("/blog/confidential-paper-binned-by-mistake-zagreb-lesson")({
  head: () => ({
    meta: [
      { title: TITLE_TAG },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: TITLE_TAG },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: HERO_ALT },
      { property: "article:published_time", content: DATE },
      { property: "article:modified_time", content: DATE },
      { property: "article:section", content: "Data Protection" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE_TAG },
      { name: "twitter:description", content: META_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: HERO_ALT },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: (jsonLd as object[]).map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    })),
  }),
  component: ZagrebLessonPage,
});

const linkClass =
  "underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss";
const h2Class = "mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl";
const bodyClass = "mt-5 text-pretty text-base leading-[1.8] text-charcoal/80";

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={linkClass}>
      {children}
    </a>
  );
}

const sources: { label: string; href: string }[] = [
  {
    label: "Jutarnji list, \"Pogledajte što je usred bijela dana završilo u kanti za smeće u centru Zagreba\" (4 October 2026)",
    href: "https://www.jutarnji.hr/vijesti/zagreb/vlaska-dokumenti-osobni-podaci-kanta-grad-zagreb-azop-nadzor-15752452",
  },
  {
    label: "Zagreb.info, \"Šokirana Zagrepčanka slikala papire u kanti za smeće\" (4 October 2026)",
    href: "https://www.zagreb.info/vijesti/sokirana-zagrepcanka-slikala-papire-u-kanti-za-smece-iz-djecjih-vrtica-i-ureda-za-sport/881002/",
  },
  {
    label: "Manx.News, \"Company Left Personal Data Behind\" (12 March 2026)",
    href: "https://www.manx.news/moving-company-left-personal-data-behind/",
  },
  {
    label: "Data Centre Insight, Fellowes analysis of ICO paper disposal breach data (3 September 2026)",
    href: "https://datacentreinsight.co.uk/2026/09/03/more-than-a-third-of-uk-paper-disposal-breaches-not-reported-to-the-ico-within-the-legal-72-hour-window/",
  },
  {
    label: "ICO, Records management and security checklist",
    href: "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security",
  },
  {
    label: "ICO, Disposal and deletion checklist",
    href: "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/",
  },
];

const faq = [
  {
    q: "Is a box marked \"for shredding\" secure enough?",
    a: "Not on its own. The ICO expects records awaiting destruction to be stored securely, for example in a locked area with restricted access. An open box can be moved, emptied or misread by anyone who walks past it.",
  },
  {
    q: "What should we do if confidential paper ends up in general waste?",
    a: "Recover it if you safely can, record what happened and assess the risk to the people affected. If the breach is likely to result in a risk to their rights and freedoms, you must report it to the ICO within 72 hours of becoming aware of it.",
  },
  {
    q: "Should our cleaners handle confidential waste?",
    a: "It is safer if they do not. Locked consoles mean cleaners never need to touch confidential paper, and the console is emptied only during a secure collection.",
  },
  {
    q: "Do you provide proof of destruction?",
    a: "Yes. Every confidential shredding collection comes with an electronic note of secure destruction for your records, so you can match it against your disposal log.",
  },
];

function ZagrebLessonPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={crumbs} />
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              Data Protection · 5 min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {H1}
            </h1>
            <Byline published={DATE} modified={DATE} />
          </header>

          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                width={1280}
                height={720}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                <p className="text-pretty text-lg leading-[1.75] text-charcoal">
                  <strong>Short answer:</strong> In late September 2026, documents holding personal details of kindergarten staff, along with contracts from Zagreb's City Office for Education, Sport and Youth, were found in a public waste bin in the centre of the Croatian capital. The city says the papers had been set aside for secure destruction, but a cleaner mistook part of the pile for ordinary rubbish. The lesson for UK businesses is simple: confidential paper is most exposed in the gap between "no longer needed" and "destroyed". It should go straight into a locked console and leave your premises only through a documented, secure collection.
                </p>

                <section id="what-happened" aria-labelledby="what-happened-h">
                  <h2 id="what-happened-h" className={h2Class}>What happened in Zagreb?</h2>
                  <p className={bodyClass}>
                    According to reports in Jutarnji list and Zagreb.info on 4 October 2026, a member of the public photographed bundles of paper sticking out of a full bin on Vlaška Street. They included personal information about nursery employees and a number of contracts belonging to the city's education office.
                  </p>
                  <p className={bodyClass}>
                    The City of Zagreb said its education office was told about the find on Wednesday 30 September. The documents were collected and stored securely, and an internal investigation began. The city explained that the paperwork was no longer needed and had been prepared for destruction in line with the rules, but a cleaning worker confused part of it with general waste and put it in a container for collection.
                  </p>
                  <p className={bodyClass}>
                    The city has sent a personal data breach report to Croatia's data protection authority and is informing the staff affected. The authority told Jutarnji list that no protective measures appear to have been taken, that the personal data was left unsupervised on a public street where anyone could read it, and that it will open a supervisory procedure once it has established who is responsible. It pointed to Article 32 of the GDPR, which deals with the security of processing.
                  </p>
                  <p className={bodyClass}>
                    <em>These details are summarised from Croatian-language reports. Quotes have been paraphrased rather than translated word for word.</em>
                  </p>
                </section>

                <section id="ready-for-shredding" aria-labelledby="ready-for-shredding-h">
                  <h2 id="ready-for-shredding-h" className={h2Class}>Why "ready for shredding" is not the same as "shredded"</h2>
                  <p className={bodyClass}>
                    Nobody in Zagreb set out to throw personal data away. The paper had already been sorted for destruction. The failure happened in the waiting period, when the pile looked like any other rubbish to someone who had not been told otherwise.
                  </p>
                  <p className={bodyClass}>
                    It is a pattern that shows up again and again. In March 2026, the Isle of Man Information Commissioner reprimanded Payroll Partners Limited after two full, unlocked shredding bins of payroll documents were left behind in an office the firm had vacated. Dip sampling of five boxes found more than 9,700 records, including names, salary information, immigration documents and photographs.
                  </p>
                  <p className={bodyClass}>
                    Closer to home, Fellowes analysed 846 paper disposal breaches reported to the UK ICO between 2019 and 2025. Health organisations accounted for 227 of them, and education and childcare for 185. In each case, paperwork with personal data was thrown away without being shredded or otherwise destroyed.
                  </p>
                </section>

                <section id="uk-guidance" aria-labelledby="uk-guidance-h">
                  <h2 id="uk-guidance-h" className={h2Class}>What does UK guidance expect for paper awaiting destruction?</h2>
                  <p className={bodyClass}>
                    The UK GDPR carries the same Article 32 security duty that the Croatian authority cited. The ICO's own audit checklists make the practical steps clear. We cover them in detail in our guide to <A href={ICO_GUIDE}>what the ICO expects when you dispose of confidential waste</A>, but the points most relevant to the Zagreb case are:
                  </p>
                  <ul className="mt-6 space-y-3 border-l border-moss/30 pl-6">
                    {[
                      ["Locked bins:", "records containing personal information should go into locked waste bins or consoles, not open bags or boxes."],
                      ["Secure storage while waiting:", "records awaiting destruction should be kept securely, for example in a locked area with restricted access, and checked on site walks."],
                      ["A disposal log:", "keep a record of confidential waste sent for destruction, and of what is still waiting and where."],
                      ["Proof:", "get a certificate of destruction for every collection and have a named person check it against your log."],
                      ["Breach reporting:", "if personal data is exposed, you must report it to the ICO within 72 hours of becoming aware of it, unless it is unlikely to result in a risk to people's rights and freedoms."],
                    ].map(([b, t]) => (
                      <li key={b} className="flex gap-3 text-base leading-[1.7] text-charcoal/80">
                        <span aria-hidden className="mt-[0.55em] h-2 w-2 shrink-0 rounded-sm border border-moss" />
                        <span><strong className="text-charcoal">{b}</strong> {t}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section id="your-business" aria-labelledby="your-business-h">
                  <h2 id="your-business-h" className={h2Class}>What this means for your business</h2>
                  <p className={bodyClass}>
                    If your team handles patient notes, staff files, safeguarding records or client paperwork, the Zagreb case is a useful prompt to look at the weakest link in your own process.
                  </p>
                  <ol className="mt-6 list-decimal space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss">
                    <li><strong className="text-charcoal">Remove the "to shred" pile altogether.</strong> Confidential paper should go directly into a locked console at the point it is no longer needed, so there is nothing for anyone to mistake for rubbish.</li>
                    <li><strong className="text-charcoal">Brief cleaners and facilities staff.</strong> Contract and agency cleaners change often. Make sure they know which containers are confidential, and that they never empty or move them.</li>
                    <li><strong className="text-charcoal">Plan moves, closures and archive clear-outs.</strong> The Isle of Man case started with an office closure. Book a one-off collection before the keys are handed back.</li>
                    <li><strong className="text-charcoal">Keep the paper trail.</strong> Match each certificate of destruction to your disposal log, so you can show what left your control and when.</li>
                  </ol>
                  <p className={bodyClass}>
                    Nurseries, clinics, dental practices and care providers are the organisations most often caught out, but the same steps apply to any office that holds personal data on paper.
                  </p>
                </section>

                <section id="faq" aria-labelledby="faq-h">
                  <h2 id="faq-h" className={h2Class}>Frequently asked questions</h2>
                  <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                    {faq.map((f) => (
                      <div key={f.q} className="py-6">
                        <h3 className="font-serif text-xl leading-snug text-moss">{f.q}</h3>
                        <p className="mt-3 text-base leading-[1.8] text-charcoal/80">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <aside
                  aria-labelledby="cta-h"
                  className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10"
                >
                  <h2 id="cta-h" className="font-serif text-2xl text-charcoal">
                    Need locked consoles or a one-off clear-out?
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    We arrange <A href="/services/confidential-waste-disposal">locked consoles, scheduled confidential shredding collections</A> and <A href="/services/one-off-shredding">one-off clear-outs</A> for offices, clinics, nurseries and care providers across the UK. Tell us what you hold and how often it builds up, and we will come back to you with a tailored quote.
                  </p>
                  <a
                    href={QUOTE_HREF}
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    Request a tailored quote
                  </a>
                </aside>

                <div className="mt-12 text-sm leading-relaxed text-charcoal/70">
                  <h2 className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">Sources</h2>
                  <ul className="mt-3 space-y-2">
                    {sources.map((s) => (
                      <li key={s.href}>
                        {s.label}:{" "}
                        <a href={s.href} target="_blank" rel="noopener" className={`${linkClass} break-all`}>
                          {s.href}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 italic">
                    This article is general information based on published news reports and ICO guidance. It is not legal advice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <RelatedArticles slugs={["ico-confidential-waste-disposal-guide", "office-laptops-lithium-batteries-safe-disposal", "ico-met-police-third-party-suppliers-disposal"]} />
        <RelatedServices slugs={["it-and-media-recycling"]} />
      </main>

      <SiteFooter />
    </div>
  );
}
